import Icon from '@components/utils/icons/icon';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { getLocaleDirection } from '@utils/locale/direction';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { useNavigationContext } from '../contexts/NavigationContext';
import { useProgressContext } from '../contexts/ProgressContext';

const Overlay = styled.div`
    z-index: 900;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    position: absolute;
    display: flex;
    align-items: end;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    padding: ${({ theme }) => theme.spacing.sm};
`;

const LeftIcon = styled(Icon)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ theme }) => theme.spacing.xs};
`;

const RightIcon = styled(Icon)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: ${({ theme }) => theme.spacing.xs};
`;

type Props = {
    onPlay: () => void;
    onPause: () => void;
};

const NavigationOverlay: FC<Props> = ({ onPlay, onPause }) => {
    const { palette } = useTheme();
    const timeoutRef = useRef<NodeJS.Timeout>();
    const router = useRouter();
    const isRtl = getLocaleDirection(router.locale) === 'rtl';
    const { next, previous, canGoBack, canGoForward } = useNavigationContext();
    const { isPaused } = useProgressContext();

    const handleKeyDown = useCallback(
        async (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                isRtl ? next() : previous();
            } else if (e.key === 'ArrowRight') {
                isRtl ? previous() : next();
            } else if (e.key === 'Escape') {
                router.back();
            }
        },
        [router, isRtl, previous, next]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const debouncePause = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();

        if (e.type === 'touchstart') {
            e = e as React.TouchEvent;
        }

        timeoutRef.current = setTimeout(() => {
            onPause();
        }, 500);
    };

    const mouseUpFullPage = async (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        timeoutRef.current && clearTimeout(timeoutRef.current);

        if (isPaused) {
            onPlay();
        } else {
            let clickX: number;

            if (e.type === 'mouseup') {
                e = e as React.MouseEvent;
                clickX = e.clientX;
            } else {
                e = e as React.TouchEvent;
                clickX = e.changedTouches[0].clientX;
            }

            if (window.innerWidth / 2 - clickX < 0) {
                isRtl ? previous() : next();
            } else {
                isRtl ? next() : previous();
            }
        }
    };

    const showChevronLeft = useMemo(() => {
        return isRtl ? canGoForward : canGoBack;
    }, [canGoBack, canGoForward, isRtl]);

    const showChevronRight = useMemo(() => {
        return isRtl ? canGoBack : canGoForward;
    }, [canGoBack, canGoForward, isRtl]);

    return (
        <Overlay
            onTouchStart={debouncePause}
            onMouseDown={debouncePause}
            onTouchEnd={mouseUpFullPage}
            onMouseUp={mouseUpFullPage}
        >
            {showChevronLeft && (
                <LeftIcon
                    name='chevron_left'
                    size='medium'
                    color={palette.common.white}
                />
            )}
            {showChevronRight && (
                <RightIcon
                    name='chevron_right'
                    size='medium'
                    color={palette.common.white}
                />
            )}
        </Overlay>
    );
};

export default NavigationOverlay;
